import React from 'react';
import { FormatCurrency } from '../../../Components/CurrencyFormatter';
import { WIZARD_TYPES } from '../../../constants/planWizardTypes';

const getBillingPeriodLabel = (billingPeriod, t) => {
  const code = billingPeriod?.code || billingPeriod;
  if (!code) {
    return null;
  }
  const productKey = `product.${String(code).toLowerCase()}`;
  const translated = t(productKey);
  return translated !== productKey ? translated : code;
};

const PlanWizardReviewCard = ({
  wizardType,
  values,
  t,
  currencySymbol,
  currencyFormat,
  equalParts,
  recoverMissed,
  planName,
  planDescription,
}) => {
  const policyCode = values.policy?.code;
  const showDeductions = values.deductions != null
    && values.deductions !== ''
    && !(policyCode === 'FIXED' && values.cost == null);

  const renderPolicyBadges = () => {
    if (policyCode === 'TARGET' || wizardType === WIZARD_TYPES.TARGET) {
      return (
        <div className="flex flex-wrap gap-1" style={{ paddingBottom: '0.2rem' }}>
          <p className="text-left policy mb-0">{t('billing', { val: 'TARGET' })}</p>
          <p className="text-left policy target-stat mb-0">
            {(values.targetEqualParts ?? equalParts) ? t('product.equal') : t('product.varying')}
          </p>
          {(values.targetEqualParts ?? equalParts) && (values.recoverMissed || recoverMissed) && (
            <p className="text-left policy target-stat-miss mb-0">{t('product.recovery')}</p>
          )}
        </div>
      );
    }

    if (policyCode === 'FIXED') {
      return <p className="text-left policy mb-0">{t('products.fixed_billing').toUpperCase()}</p>;
    }
    if (policyCode === 'CUSTOM') {
      return <p className="text-left policy mb-0">{t('products.custom_billing').toUpperCase()}</p>;
    }
    if (policyCode === 'FLEXIBLE' || values.flex) {
      return <p className="text-left policy mb-0">{t('billing', { val: 'FLEXIBLE' })}</p>;
    }
    if (policyCode) {
      return <p className="text-left policy mb-0">{t('billing', { val: policyCode })}</p>;
    }
    return null;
  };

  const renderAmount = () => {
    if (policyCode === 'TARGET' || wizardType === WIZARD_TYPES.TARGET) {
      return <span className="planCost mt-0">{t('products.target-fixed')}</span>;
    }
    if (policyCode === 'CUSTOM' && (values.cost == null || values.cost === '')) {
      return <span className="planCost mt-0">{t('amount_fixed')}</span>;
    }
    if (policyCode === 'FLEXIBLE' || values.flex) {
      return <span className="planCost mt-0">{t('products.flexible-amount')}</span>;
    }
    if (policyCode === 'FIXED' && values.cost != null && values.cost !== '') {
      return (
        <FormatCurrency
          amount={values.cost}
          currencySymbol={currencySymbol}
          currencyFormat={currencyFormat}
          className="planCost mt-0"
        />
      );
    }
    return null;
  };

  const periodLabel = getBillingPeriodLabel(values.billingPeriod, t);

  const setupAmount = values.feeCheck && values.setup != null && values.setup !== ''
    ? values.setup
    : null;

  return (
    <div className="plan-wizard-review-card plan-info-main">
      <div style={{ margin: '0.3rem 0rem 0.8rem 0rem' }}>
        {planName && (
          <p className="plan-wizard-review-plan-name">{planName}</p>
        )}
        {planDescription && (
          <p className="plan-wizard-review-plan-description">{planDescription}</p>
        )}
        {renderPolicyBadges()}
        <div>
          <div className="plan-amt">{renderAmount()}</div>
          {periodLabel && (
            <span className="planBillingPeriod pb-0 pt-0">{periodLabel}</span>
          )}
        </div>
        {showDeductions && (
          <div className="text-left">
            <p id="deductions-text">
              {values.deductions > 1
                ? t('product.deduction-val_plural', { count: values.deductions })
                : t('product.deduction-val', { count: values.deductions })}
            </p>
          </div>
        )}
      </div>

      {setupAmount != null && (
        <div className="setup_fee_div" id="setup-m-b">
          <span className="setup_fee_tag">{t('common.setup_fee_label')}</span>
          <FormatCurrency
            amount={setupAmount}
            currencySymbol={currencySymbol}
            currencyFormat={currencyFormat}
            className="setup_fee_value"
          />
        </div>
      )}

      {values.graceDays != null && values.graceDays !== '' && Number(values.graceDays) > 0 && (
        <div style={{ display: 'flex', flexDirection: 'row', paddingBottom: '0.2rem' }}>
          <span className="graceDaysLabel">{t('product.graceDaysLabel')}:</span>
          <span className="planGraceDays">
            {values.graceDays} {t('products.day')}
          </span>
        </div>
      )}

      {values.reminderDays != null && values.reminderDays !== '' && Number(values.reminderDays) > 0 && (
        <div style={{ display: 'flex', flexDirection: 'row' }}>
          <span className="reminderDaysLabel">{t('product.reminderDaysLabel')}:</span>
          <span className="planReminderDays">
            {values.reminderDays} {t('products.day')}
          </span>
        </div>
      )}
    </div>
  );
};

export default PlanWizardReviewCard;
